import { z } from 'zod'
import { DogGender } from '../../laravel-data-exporter/types'

export const DogBreedSizeClassifications = {
  TEACUP: 'teacup',
  TOY: 'toy',
  SMALL: 'small',
  SMALL_MEDIUM: 'small-medium',
  MEDIUM: 'medium',
  MEDIUM_LARGE: 'medium-large',
  LARGE: 'large',
  GIANT: 'giant',
} as const

export interface CubicCoefficients {
  breedSize: typeof DogBreedSizeClassifications[keyof typeof DogBreedSizeClassifications]
  power3: number
  power2: number
  power1: number
  constant: number
}

export const GROWTH_CURVE_COEFFICIENTS: CubicCoefficients[] = [
  {
    breedSize: 'teacup',
    power3: 0.0000308575,
    power2: -0.00436,
    power1: 0.231841,
    constant: -0.54667,
  },
  {
    breedSize: 'toy',
    power3: 0.0000528278,
    power2: -0.00823,
    power1: 0.447786,
    constant: -1.36262,
  },
  {
    breedSize: 'small',
    power3: 0.0000691957,
    power2: -0.01191,
    power1: 0.714325,
    constant: -2.7331,
  },
  {
    breedSize: 'small-medium',
    power3: 0.0000309365,
    power2: -0.0093304413,
    power1: 0.7909941213,
    constant: -3.4682902392,
  },
  {
    breedSize: 'medium',
    power3: 0.0000998378,
    power2: -0.02007,
    power1: 1.34834,
    constant: -6.68059,
  },
  {
    breedSize: 'medium-large',
    power3: 0.0000941894,
    power2: -0.02053,
    power1: 1.483759,
    constant: -8.23299,
  },
  {
    breedSize: 'large',
    power3: 0.000208373,
    power2: -0.03843,
    power1: 2.356884,
    constant: -12.6185,
  },
  {
    breedSize: 'giant',
    power3: 0.000228563,
    power2: -0.04308,
    power1: 2.680066,
    constant: -10.4465,
  },
]

export interface BreedSize {
  id: string
  legacyId: number
  name: typeof DogBreedSizeClassifications[keyof typeof DogBreedSizeClassifications]
  slug: typeof DogBreedSizeClassifications[keyof typeof DogBreedSizeClassifications]
  adultMinKg: number
  adultMaxKg: number
  adultAgeMonths: number
  adultAgeWeeks: number
}

export const BREED_SIZES: BreedSize[] = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    legacyId: 1,
    name: 'teacup',
    slug: 'teacup',
    adultMinKg: 0,
    adultMaxKg: 5,
    adultAgeMonths: 9,
    adultAgeWeeks: 39,
  },
  {
    id: '00000000-0000-0000-0000-000000000002',
    legacyId: 2,
    name: 'toy',
    slug: 'toy',
    adultMinKg: 5,
    adultMaxKg: 8,
    adultAgeMonths: 10,
    adultAgeWeeks: 44,
  },
  {
    id: '00000000-0000-0000-0000-000000000003',
    legacyId: 3,
    name: 'small',
    slug: 'small',
    adultMinKg: 8,
    adultMaxKg: 14,
    adultAgeMonths: 12,
    adultAgeWeeks: 52,
  },
  {
    id: '00000000-0000-0000-0000-000000000004',
    legacyId: 4,
    name: 'small-medium',
    slug: 'small-medium',
    adultMinKg: 14,
    adultMaxKg: 20,
    adultAgeMonths: 12,
    adultAgeWeeks: 52,
  },
  {
    id: '00000000-0000-0000-0000-000000000005',
    legacyId: 5,
    name: 'medium',
    slug: 'medium',
    adultMinKg: 20,
    adultMaxKg: 25,
    adultAgeMonths: 15,
    adultAgeWeeks: 65,
  },
  {
    id: '00000000-0000-0000-0000-000000000006',
    legacyId: 6,
    name: 'medium-large',
    slug: 'medium-large',
    adultMinKg: 25,
    adultMaxKg: 30,
    adultAgeMonths: 15,
    adultAgeWeeks: 65,
  },
  {
    id: '00000000-0000-0000-0000-000000000007',
    legacyId: 7,
    name: 'large',
    slug: 'large',
    adultMinKg: 30,
    adultMaxKg: 40,
    adultAgeMonths: 18,
    adultAgeWeeks: 78,
  },
  {
    id: '00000000-0000-0000-0000-000000000008',
    legacyId: 8,
    name: 'giant',
    slug: 'giant',
    adultMinKg: 40,
    adultMaxKg: 100,
    adultAgeMonths: 18,
    adultAgeWeeks: 78,
  },
]

export const DogBreedSizeClassification = z.nativeEnum(DogBreedSizeClassifications)
export type DogBreedSizeClassification = z.infer<typeof DogBreedSizeClassification>

export const WeightMilestoneSchema = z.object({
  targetWeightKg: z.number().positive(),
  estimatedAgeWeeks: z.number().int().nonnegative(),
  label: z.string(),
  mealWeightV1: z.number().positive(),
  mealWeightV2: z.number().positive(),
})
export type WeightMilestone = z.infer<typeof WeightMilestoneSchema>

export const PuppyBreedSchema = z.object({
  slug: z.string(),
  sizeClassification: DogBreedSizeClassification,
  adultWeightMin: z.number().nonnegative(),
  adultWeightMax: z.number().nonnegative(),
  adultAgeMonthsMax: z.number().nonnegative(),
})
export type PuppyBreed = z.infer<typeof PuppyBreedSchema>

export const PuppyRecommendationResponseSchema = z.object({
  breeds: PuppyBreedSchema.array(),
  gender: z.nativeEnum(DogGender),
  breedSizeClassification: DogBreedSizeClassification,
  currentWeight: z.number().positive(),
  currentAgeMonths: z.number().int().nonnegative(),
  currentAgeWeeks: z.number().int().nonnegative(),
  expectedAdultAgeMonths: z.number().int().positive(),
  expectedAdultAgeWeeks: z.number().int().positive(),
  expectedAdultWeightInKg: z.number().positive(),
  expectedAdultMealWeight: z.number().positive(),
  breedAdultMealWeight: z.number().positive(),
  weightMilestones: WeightMilestoneSchema.array(),
})
export type PuppyRecommendationResponse = z.infer<typeof PuppyRecommendationResponseSchema>
